// src/services/redux/api/baseApi.js
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { API_BASE_URL } from '../../config';
import {
  customerEndpoints,
  agencyEndpoints,
  AreaEndpoints,
  GroupEndpoints,
  branchEndpoints,
  authEndpoints,
  productMasterEndpoints,
  importFileEndpoints,
  themeMasterEndpoints,
  riderEndpoints,
  LifePolicyEntryEndpoints,
  PremiumDepositEndpoints,
  fupUpdateEndpoints, // added fup endpoints
  policyReportEndpoints
} from './endpoints';

// <-- IMPORTANT: import clearCredentials from your auth slice (adjust path if needed)
import { clearCredentials } from '../slices/authSlice';

// Core fetch with header preparation
const baseFetch = fetchBaseQuery({
  baseUrl: API_BASE_URL,
  prepareHeaders: (headers, { getState }) => {
    // Prefer token from Redux auth slice, fallback to localStorage
    const tokenFromState = getState().auth?.token;
    let token = tokenFromState;

    if (!token) {
      try {
        const rawAuth = localStorage.getItem('auth');
        if (rawAuth) {
          const parsed = JSON.parse(rawAuth);
          token = parsed?.token ?? token;
        }
      } catch (err) {
        // ignore parse errors
      }
    }

    if (!token) {
      const rawToken = localStorage.getItem('token');
      if (rawToken) token = rawToken;
    }
    
    // Dev fallback: if no token found in state/localStorage, allow an optional
    // environment-provided token for local development only. Set REACT_APP_DEV_TOKEN
    // in your .env to use this. Do NOT commit real tokens to source control.
    if (!token && typeof process !== 'undefined' && process.env && process.env.REACT_APP_DEV_TOKEN) {
      token = process.env.REACT_APP_DEV_TOKEN;
    }

    if (token) {
      // Dev-only debug: show where the token was sourced from and a masked preview
      try {
        if (typeof process !== 'undefined' && process.env && process.env.NODE_ENV !== 'production') {
          const rawAuth = (() => {
            try { return localStorage.getItem('auth'); } catch (e) { return null; }
          })();
          const rawToken = (() => {
            try { return localStorage.getItem('token'); } catch (e) { return null; }
          })();
          const source = tokenFromState ? 'redux' : (rawAuth ? 'localStorage:auth' : (rawToken ? 'localStorage:token' : (process.env.REACT_APP_DEV_TOKEN ? 'env:REACT_APP_DEV_TOKEN' : 'unknown')));
          const masked = String(token).length > 12 ? `${String(token).slice(0,8)}...${String(token).slice(-4)}` : String(token);
          // use console.debug so it's easy to filter and safe for development
          // eslint-disable-next-line no-console
          console.debug(`[baseApi] prepareHeaders token source=${source} tokenExists=${!!token} tokenPreview=${masked}`);
        }
      } catch (dbgErr) {
        // ignore debug logging errors
      }

      headers.set('Authorization', `Bearer ${token}`);
    }

    headers.set('Accept', 'application/json');
    return headers;
  },
});

// Wrapper to auto-handle 401 (clears credentials)
const baseQueryWithReauth = async (args, api, extraOptions) => {
  const result = await baseFetch(args, api, extraOptions);

  if (result?.error?.status === 401) {
    try {
      const tokenInState = api.getState().auth?.token;
      let tokenInStorage = null;
      try {
        const raw = localStorage.getItem('auth');
        tokenInStorage = raw ? JSON.parse(raw).token : null;
      } catch {}
      const fallbackToken = tokenInStorage || localStorage.getItem('token');

      console.warn(
        '[baseApi] 401 detected:',
        args,
        ' tokenInState:',
        !!tokenInState,
        ' tokenInStorage:',
        !!fallbackToken
      );

      if (tokenInState || fallbackToken) {
        api.dispatch(clearCredentials());
      } else {
        console.warn(
          '[baseApi] Skipping clearCredentials() because no token found in state or localStorage.'
        );
      }
    } catch (err) {
      console.warn('Failed to dispatch clearCredentials', err);
    }
  }

  return result;
}; // <-- function closed properly

// Create the base API slice
export const baseApi = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth,

  tagTypes: [
    'Customer',
    'Agency',
    'Area',
    'GroupMaster',
    'Branch',
    'Group',
    'Auth',
    'product',
    'theme',
    'LifePolicyEntry',
    'PremiumDepositEntry',
    'Product',
    'Policy',
    'Theme',
    'policyReport',
  ],

  endpoints: (builder) => ({
    ...customerEndpoints(builder),
    ...agencyEndpoints(builder),
    ...AreaEndpoints(builder),
    ...GroupEndpoints(builder),
    ...branchEndpoints(builder),
    ...authEndpoints(builder),
    ...productMasterEndpoints(builder),
  ...importFileEndpoints(builder),
    ...riderEndpoints(builder),
    ...themeMasterEndpoints(builder),
    ...LifePolicyEntryEndpoints(builder),
    ...PremiumDepositEndpoints(builder),
    ...fupUpdateEndpoints(builder),
    // (removed duplicate themeMasterEndpoints)
    ...policyReportEndpoints(builder),

  }),
});

// Export hooks
export const {
  // Customer hooks
  useGetCustomersQuery,
  useGetCustomerByIdQuery,
  useSaveCustomerMutation,
  useDeleteCustomerMutation,
  useBulkDeleteCustomersMutation,

  // Agency hooks
  useGetAgenciesQuery,
  useGetAgencyByIdQuery,
  useSaveAgencyMutation,
  useUpdateAgencyMutation,
  useDeleteAgencyMutation,
  useBulkDeleteAgencyMutation,
  useGetAgencyPerformanceQuery,
  useGetAgencyAgentsQuery,

  // Area hooks
  useGetAreasQuery,
  useGetAreaByIdQuery,
  useSaveAreaMutation,
  useUpdateAreaMutation,
  useDeleteAreaMutation,
  useBulkDeleteAreaMutation,

  // Group hooks
  useGetGroupsQuery,
  useGetGroupByIdQuery,
  useSaveGroupMutation,
  useUpdateGroupMutation,
  useGetCustomerByGroupIdQuery,
  useDeleteGroupMutation,
  useBulkDeleteGroupsMutation,

  // Branch hooks
  useGetBranchesQuery,
  useGetBranchByIdQuery,
  useSaveBranchMutation,
  useUpdateBranchMutation,
  useDeleteBranchMutation,
  useBulkDeleteBranchMutation,

  // Auth hooks
  useLoginMutation,
  useRegisterMutation,

  // product hooks
  useGetProductsQuery,
  useGetProductByIdQuery,
  useSaveProductMutation,
  useUpdateProductMutation,
  useDeleteProductMutation,
  useBulkDeleteProductsMutation,

  // life policy entry hooks
  useGetLifePolicyEntryQuery,
  useGetLifePolicyEntryByIdQuery,
  useSaveLifePolicyEntryMutation,
  useUpdateLifePolicyEntryMutation,
  useDeleteLifePolicyEntryMutation,
  useBulkDeleteLifePolicyEntryMutation,

  // premium deposit entry hooks
  useGetPremiumDepositEntryQuery,
  useGetPremiumDepositEntryByIdQuery,
  useSavePremiumDepositEntryMutation,
  useUpdatePremiumDepositEntryMutation,
  useDeletePremiumDepositEntryMutation,
  useBulkDeletePremiumDepositEntryMutation,

  // FUP / Policy hooks
  useGetFupPoliciesQuery,
  useBatchUpdateFupMutation,
  useDeletePolicyMutation,
  useBulkDeletePolicyMutation,

  // admin/theme hooks
  useGetThemesQuery,
  useCreateOrUpdateThemeMutation,
  useDeleteThemeMutation,

  useGetAvailableRidersQuery,



  // Add these near other exports:
  useGetPolicyReportQuery,
  usePreviewPolicyReportQuery,
  useLazyPreviewPolicyReportQuery,
  useExportPolicyReportMutation,
  useGetExportStatusQuery,
  useDownloadExportQuery,

  useGetPremiumDueReportQuery,
  usePreviewPremiumDueReportQuery,
  useLazyPreviewPremiumDueReportQuery,
  useExportPremiumDueReportMutation,
  useGetLapsedPoliciesReportQuery,
  usePreviewLapsedPoliciesReportQuery,
  useLazyPreviewLapsedPoliciesReportQuery,
  useExportLapsedPoliciesReportMutation,
  useGetPremiumOutstandingReportQuery,
  usePreviewPremiumOutstandingReportQuery,
  useLazyPreviewPremiumOutstandingReportQuery,
  useExportPremiumOutstandingReportMutation,


  // Import-file hooks
  useUploadExcelMutation,
  useGetBatchesQuery,
  useValidateInsurersMutation,
  useValidateAgenciesMutation,
  useValidateProductsMutation,
  useValidateFupMutation,
  useValidateCustomersMutation,
  useGetInvalidInsurersMutation,
  useGetInvalidProductsMutation,
  useGetInvalidAgenciesMutation,
  useGetInvalidCustomersMutation,
  useGetInvalidFupMutation,
  useApplyCorrectionMutation,
  useFinishImportMutation,
} = baseApi;
